import type { RegulationAskResponse } from "@hp/api-client";

/** One message of a saved HP Regulation conversation (same shape the chat page shows). */
export type StoredChatMessage =
  | { id: string; role: "user"; text: string }
  | { id: string; role: "assistant"; response: RegulationAskResponse }
  | { id: string; role: "error"; text: string; retryable: boolean };

export interface StoredConversation {
  id: string;
  title: string;
  updatedAt: number;
  messages: StoredChatMessage[];
}

const KEY_PREFIX = "hp_regulation_chat_history";
const MAX_CONVERSATIONS = 500;

/** Kept per signed-in user in this browser, as a mirror of what the server keeps (the server is the record; see mergeConversations). */
const keyFor = (userId: string) => `${KEY_PREFIX}:${userId}`;

export function newConversationId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `c${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

export function newMessageId(): string {
  return `m${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

/** The first question, cut to one short line: what the history list shows. */
export function titleOf(messages: StoredChatMessage[]): string {
  const first = messages.find((m): m is Extract<StoredChatMessage, { role: "user" }> => m.role === "user");
  const line = (first?.text ?? "New chat").split("\n").find((l) => l.trim()) ?? "New chat";
  const text = line.trim().replace(/\s+/g, " ");
  return text.length > 70 ? `${text.slice(0, 67)}…` : text;
}

export function loadConversations(userId: string): StoredConversation[] {
  try {
    const raw = localStorage.getItem(keyFor(userId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return (parsed as StoredConversation[])
      .filter((c) => c && typeof c.id === "string" && Array.isArray(c.messages))
      .sort((a, b) => b.updatedAt - a.updatedAt);
  } catch {
    return [];
  }
}

function write(userId: string, conversations: StoredConversation[]): void {
  const kept = conversations.slice(0, MAX_CONVERSATIONS);
  // Storage can be full or blocked; drop the oldest until it fits, then give up quietly (history is a convenience).
  for (let n = kept.length; n >= 0; n -= Math.max(1, Math.ceil(n / 4))) {
    try {
      localStorage.setItem(keyFor(userId), JSON.stringify(kept.slice(0, n)));
      return;
    } catch {
      if (n === 0) return;
    }
  }
}

/** Saves (or updates) one conversation; an empty conversation is not saved. Returns the new list, newest first. */
export function saveConversation(userId: string, id: string, messages: StoredChatMessage[]): StoredConversation[] {
  const others = loadConversations(userId).filter((c) => c.id !== id);
  if (messages.length === 0) return others;
  const next = [{ id, title: titleOf(messages), updatedAt: Date.now(), messages }, ...others];
  write(userId, next);
  return next;
}

export function deleteConversation(userId: string, id: string): StoredConversation[] {
  const next = loadConversations(userId).filter((c) => c.id !== id);
  write(userId, next);
  return next;
}

export function clearConversations(userId: string): void {
  try {
    localStorage.removeItem(keyFor(userId));
  } catch {
    /* nothing to clear */
  }
}

/** "Today", "Yesterday", "Earlier this week" or the date, to group the list. */
export function dayLabel(updatedAt: number, now = new Date()): string {
  const start = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((start(now) - start(new Date(updatedAt))) / 86_400_000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return "Earlier this week";
  return new Date(updatedAt).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

/** Deletions the server has not confirmed yet (the request failed or never went): chat id (or "*" for all) -> when it was deleted here.
 * Checked against what the server sends back, so a failed delete is retried and a deleted chat never reappears. */
const tombstoneKey = (userId: string) => `${KEY_PREFIX}:deleted:${userId}`;

export function pendingDeletions(userId: string): Record<string, number> {
  try {
    const raw = localStorage.getItem(tombstoneKey(userId));
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    return parsed && typeof parsed === "object" ? (parsed as Record<string, number>) : {};
  } catch {
    return {};
  }
}

export function rememberDeletion(userId: string, id: string): void {
  try {
    localStorage.setItem(tombstoneKey(userId), JSON.stringify({ ...pendingDeletions(userId), [id]: Date.now() }));
  } catch {
    /* without storage the server's answer decides */
  }
}

export function forgetDeletion(userId: string, id: string): void {
  try {
    const rest = pendingDeletions(userId);
    delete rest[id];
    localStorage.setItem(tombstoneKey(userId), JSON.stringify(rest));
  } catch {
    /* nothing kept */
  }
}

/** Whether a chat the server sent is one this browser deleted and the server has not yet confirmed: deleted here after its last change. */
export function deletedHere(pending: Record<string, number>, c: StoredConversation): boolean {
  const at = pending[c.id] ?? pending["*"];
  return at !== undefined && at >= (c.updatedAt ?? 0);
}

/** After this browser's chats went up (importLookupChats), the server's list is the record: it is written here and returned, newest first.
 * A chat missing from it was deleted on another device (or was empty); it is not kept here. */
export function replaceWithServerConversations(userId: string, fromServer: StoredConversation[]): StoredConversation[] {
  const kept = fromServer.filter((c) => c && typeof c.id === "string" && Array.isArray(c.messages)).sort((a, b) => b.updatedAt - a.updatedAt);
  write(userId, kept);
  return kept;
}
